import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type ProgressTone = 'brand' | 'accent' | 'success' | 'warning' | 'danger';

/**
 * `<nb-progress>` — linear progress / completion bar (2026-09 prototype: 8px
 * track in --surface-2, fill grows in with `nb-grow`). Used for read-receipts,
 * profile completion, shared purchases, EV charging and survey response rate.
 *
 * Accessible as `role="progressbar"` with `aria-valuenow/min/max`; give it a
 * `label` that says WHAT is progressing ("Lest av 64 % av beboerne"). The
 * percentage is never colour-only — render the number as text next to it.
 *
 * Set `[meter]="true"` for a measurement rather than a task in flight (activation
 * share, SMS cap): same bar, `role="meter"` instead of `progressbar`.
 *
 * @example <nb-progress [value]="64" label="Lest av 64 % av beboerne" />
 * @example <nb-progress [meter]="true" [value]="42" label="Aktivering 42 %" tone="warning" />
 * @example <nb-progress [value]="3" [max]="5" tone="accent" label="3 av 5 steg" />
 */
@Component({
  selector: 'nb-progress',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.role]': "meter() ? 'meter' : 'progressbar'",
    '[attr.aria-valuenow]': 'clamped()',
    '[attr.aria-valuemin]': '0',
    '[attr.aria-valuemax]': 'max()',
    '[attr.aria-label]': 'label()',
    '[class]': 'tone()',
  },
  template: `<div class="track"><div class="fill" [style.width.%]="percent()"></div></div>`,
  styleUrl: './progress.css',
})
export class NbProgress {
  /** Current value, clamped to `[0, max]`. */
  readonly value = input.required<number>();
  /** Upper bound (default 100). */
  readonly max = input(100);
  /** Accessible name — say what is progressing. */
  readonly label = input.required<string>();
  /** Fill colour family. */
  readonly tone = input<ProgressTone>('brand');
  /** Render as `role="meter"` (a level, not progress). */
  readonly meter = input(false);

  protected readonly clamped = computed(() => Math.max(0, Math.min(this.max(), this.value())));
  protected readonly percent = computed(() => (this.max() > 0 ? (this.clamped() / this.max()) * 100 : 0));
}
